import React from 'react';
import { Trash2 } from 'lucide-react';
import ItemCard from './ItemCard';
import InputField from './InputField';
import useFlipAnimation from '../hooks/useFlipAnimation';
import { ui } from '../ui';

const TH = 'px-3.5 py-2.5 text-left text-meta font-medium uppercase tracking-wide text-ink-2';

export default function DifferentialTable({
  rows, userName, identityKey, isFacilitator, revealed = true, readOnly = false,
  addItem, editItem, deleteItem, deleteRow, vote,
}) {
  const setFlipRef = useFlipAnimation();

  const canModify = (item) =>
    isFacilitator || (item.addedByKey ? item.addedByKey === identityKey : item.addedBy === userName);

  const renderColumn = (row, column, placeholder, opts = {}) => (
    <>
      <div className={`mb-3 ${opts.scroll === false ? '' : 'max-h-96 overflow-y-auto'}`}>
        {row[column].map((item) => (
          <ItemCard
            key={item.id}
            item={item}
            onVote={(v) => vote(row.id, column, item.id, v)}
            onDelete={() => deleteItem(row.id, column, item.id, item.private)}
            onEdit={(text) => editItem(row.id, column, item.id, text, item.private)}
            canModify={canModify(item)}
            emphasis={column === 'diseases' || column === 'etiologicalClass'}
            isSmall={opts.isSmall}
            revealed={revealed}
            readOnly={readOnly}
          />
        ))}
      </div>
      {!readOnly && (!opts.singleEntry || row[column].length === 0) && (
        <InputField placeholder={placeholder} onSubmit={(text) => addItem(row.id, column, text)} />
      )}
    </>
  );

  return (
    <div className={`${ui.panel} overflow-hidden`}>
      <div className="overflow-x-auto -webkit-overflow-scrolling-touch">
        <table className="w-full min-w-[800px]">
          <thead>
            <tr className="bg-page border-b border-line">
              <th className={`${TH} w-[20%]`}>Etiological Class</th>
              <th className={`${TH} w-[20%]`}>Diseases</th>
              <th className={`${TH} w-[30%]`}>Supporting</th>
              <th className={`${TH} w-[30%]`}>Opposing</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} ref={setFlipRef(row.id)} className="group border-t border-line bg-surface">
                <td className="p-3.5 align-top">
                  {isFacilitator && !readOnly && (
                    <button
                      onClick={() => deleteRow(row.id, true)}
                      className={`card-tools ${ui.iconBtn} w-7 h-7 float-right hover:text-flag-red`}
                      aria-label="Delete row"
                      title="Delete this row (facilitator)"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {renderColumn(row, 'etiologicalClass', 'Add etiological class...', { singleEntry: true, scroll: false })}
                </td>
                <td className="border-l border-line p-3.5 align-top">
                  {renderColumn(row, 'diseases', 'Add disease...')}
                </td>
                <td className="border-l border-line p-3.5 align-top">
                  {renderColumn(row, 'supporting', 'Add supporting...', { isSmall: true })}
                </td>
                <td className="border-l border-line p-3.5 align-top">
                  {renderColumn(row, 'opposing', 'Add opposing...', { isSmall: true })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
